import { type ChangeEvent, Component, type FocusEvent } from "react";
import {
	sendMessageAllowed,
	sendReplyMessageAsync,
} from "../../api/secure-messaging-api";
import ModalDialog from "../../components/modal-dialog";
import SubmittedMessageResultModalDialog from "../../components/submitted-message-result-modal-dialog";
import ThreeDotsLoadingSpinner from "../../components/three-dots-loading-spinner";
import events from "../../constants/events";
import translate, { epiTranslations } from "../../constants/translations";
import hideAttachments from "../../helpers/attachment-helper";
import type { CustomerContext } from "../../types";
import FileAttachmentDropDown from "../file-attachments/file-attachment-dropdown";
import type { UploadItem } from "../file-attachments/upload-progress-bar";

interface SendReplyProps {
	customer: CustomerContext;
	cultureCode: string;
	prefilledReply?: string;
	conversationId: string;
	expanded: boolean;
	category?: string;
	onReplySent?: () => void;
}

interface SendReplyState {
	body: string;
	prefilledReply: string;
	busy: boolean;
	sent: boolean;
	sendFailed: boolean;
	sendAllowed: boolean;
	attachments: unknown[];
	uploads: UploadItem[];
	isWindowReloadInitiated?: boolean;
}

class SendReply extends Component<SendReplyProps, SendReplyState> {
	private mounted = false;

	constructor(props: SendReplyProps) {
		super(props);

		const { cultureCode, prefilledReply } = props;

		this.onInputChange = this.onInputChange.bind(this);
		this.setReplyBody = this.setReplyBody.bind(this);
		this.onSendMessageClick = this.onSendMessageClick.bind(this);
		this.hasActiveAttachmentUploads =
			this.hasActiveAttachmentUploads.bind(this);
		this.updateUploads = this.updateUploads.bind(this);
		this.updateAttachments = this.updateAttachments.bind(this);
		this.onClickConfirmationModalButton =
			this.onClickConfirmationModalButton.bind(this);

		this.translate = (term: string) => translate(cultureCode, term);

		this.state = {
			body: "",
			prefilledReply: prefilledReply || "",
			busy: false,
			sent: false,
			sendFailed: false,
			sendAllowed: false,
			attachments: [],
			uploads: [],
		};
	}

	private readonly translate: (term: string) => string;

	componentDidMount() {
		const { customer } = this.props;
		this.mounted = true;

		sendMessageAllowed(customer).then((sendAllowed) => {
			if (this.mounted) {
				this.setState({ sendAllowed });
			}
		});
	}

	componentDidUpdate(prevProps: Readonly<SendReplyProps>) {
		const { expanded } = this.props;
		const { sent } = this.state;

		if (prevProps.expanded !== expanded && !expanded && sent) {
			this.setState({ sent: false, sendFailed: false });
		}
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	onInputChange(event: ChangeEvent<HTMLTextAreaElement>) {
		const { name, value } = event.target;

		if (name === "body") {
			this.setState({ body: value });
		}
	}

	onSendMessageClick() {
		const { customer, conversationId } = this.props;
		const { body, attachments } = this.state;

		this.setState({ busy: true });

		const sendReplyAndRetryOnFailure = async (
			tries: number,
		): Promise<Response> => {
			try {
				const response = await sendReplyMessageAsync(
					customer,
					conversationId,
					body,
					attachments,
				);
				if (response.status === 200 || tries === 1) {
					return response;
				}

				return sendReplyAndRetryOnFailure(tries - 1);
			} catch (error) {
				if (tries === 1) {
					throw error;
				}

				return sendReplyAndRetryOnFailure(tries - 1);
			}
		};

		sendReplyAndRetryOnFailure(2)
			.then((response) => {
				if (response.status === 200) {
					this.onMessageSent();
				} else {
					this.onMessageSendFailed();
				}
			})
			.catch(() => {
				this.onMessageSendFailed();
			});
	}

	onMessageSent() {
		this.setState({
			busy: false,
			sent: true,
			sendFailed: false,
			body: "",
			attachments: [],
			uploads: [],
		});
		window.dispatchEvent(new Event(events.replySent));
	}

	onMessageSendFailed() {
		this.setState({
			busy: false,
			sent: false,
			sendFailed: true,
		});
		window.dispatchEvent(new Event(events.replySendFailed));
	}

	updateUploads(uploads: UploadItem[]) {
		this.setState({ uploads });
	}

	updateAttachments(attachments: unknown[]) {
		this.setState({ attachments });
	}

	hasActiveAttachmentUploads() {
		const { uploads } = this.state;

		return uploads.some(
			(uploadObj) =>
				!uploadObj.uploadCompleted &&
				!uploadObj.uploadFailed &&
				!uploadObj.uploadCancelled,
		);
	}

	messageBodyIsValid() {
		const { body } = this.state;

		return body.trim() !== "";
	}

	sendDisabled() {
		const { busy, sendAllowed } = this.state;

		return (
			!sendAllowed ||
			busy ||
			!this.messageBodyIsValid() ||
			this.hasActiveAttachmentUploads()
		);
	}

	attachmentsDisabled() {
		const { customer } = this.props;
		const { sendAllowed } = this.state;

		return !!customer.isInternalUser || !sendAllowed;
	}

	shouldReplyModalBeVisible() {
		const { sent, sendFailed } = this.state;

		return sent || sendFailed;
	}

	onClickConfirmationModalButton() {
		if (this.state.sent) {
			this.props.onReplySent?.();
		}
		this.setState({ sent: false, sendFailed: false });
	}

	setReplyBody(_event: FocusEvent<HTMLTextAreaElement>) {
		const { prefilledReply, body } = this.state;
		if (prefilledReply && !body) {
			this.setState({ body: prefilledReply });
		}
	}

	buttonLoader() {
		return (
			<ThreeDotsLoadingSpinner css="height: 2rem; width: 2rem; margin-right: 1rem;" />
		);
	}

	prefilledBanner() {
		return (
			<div className="if alert-banner info">
				<button
					type="button"
					className="if close"
					onClick={(e) => {
						const banner = (e.target as HTMLElement)
							.parentNode as HTMLElement | null;
						if (banner) {
							banner.style.display = "none";
						}
					}}
				>
					<span className="if axe sr-only">Close</span>
				</button>
				<span className="if title">{this.translate("PrefilledInfoTitle")}</span>
				<p>{this.translate("PrefilledInfo")}</p>
			</div>
		);
	}

	render() {
		const { cultureCode, customer, category } = this.props;
		const {
			body,
			prefilledReply,
			sent,
			busy,
			sendAllowed,
			uploads,
			attachments,
			isWindowReloadInitiated,
		} = this.state;
		const disabled = !sendAllowed;

		const modalText = sent
			? {
					mobileIcon: ModalDialog.Icon.success,
					title: epiTranslations.sendReply.successHeaderText,
					text: epiTranslations.sendReply.successBodyText,
					buttonText: epiTranslations.sendReply.successButtonText,
				}
			: {
					mobileIcon: ModalDialog.Icon.failure,
					title: epiTranslations.sendReply.failureHeaderText,
					text: epiTranslations.sendReply.failureBodyText,
					buttonText: epiTranslations.sendReply.failureButtonText,
				};

		return (
			<div>
				<SubmittedMessageResultModalDialog
					visible={this.shouldReplyModalBeVisible()}
					loading={!!isWindowReloadInitiated}
					{...modalText}
					closeButtonAriaLabel={epiTranslations.sendReply.closeButtonAriaLabel}
					onClickCloseButton={this.onClickConfirmationModalButton}
					onClickMainButton={this.onClickConfirmationModalButton}
				/>

				<div className="if u-margin-top--smallest">
					<h5 className="if heading">{this.translate("ReplyToHeader")}</h5>
					{prefilledReply && this.prefilledBanner()}
					<textarea
						id="body"
						name="body"
						value={body}
						onFocus={this.setReplyBody}
						onChange={this.onInputChange}
						disabled={disabled}
						rows={9}
						placeholder={
							prefilledReply
								? prefilledReply
								: this.translate("ReplyToMessageBodyPlaceholder")
						}
						aria-invalid={!this.messageBodyIsValid()}
						aria-required="true"
						maxLength={10000}
						className="gb textarea fluid"
					/>
				</div>
				{!hideAttachments(category || "") && (
					<div className="if u-margin-top--small">
						<FileAttachmentDropDown
							cultureCode={cultureCode}
							customer={customer}
							attachmentsDisabled={this.attachmentsDisabled()}
							uploads={uploads}
							attachments={attachments}
							updateUploads={this.updateUploads}
							updateAttachments={this.updateAttachments}
						/>
					</div>
				)}
				<div className="if u-margin-top--40">
					<button
						id="send"
						type="button"
						onClick={this.onSendMessageClick}
						aria-disabled={this.sendDisabled()}
						disabled={this.sendDisabled()}
						className="if button primary"
					>
						{busy && this.buttonLoader()}
						{this.translate("SendReply")}
					</button>
				</div>
			</div>
		);
	}
}

export default SendReply;
